import { ReactNode, useMemo } from 'react';

import { PerpetualMarketSummary } from '#/api/markets-summary';
import {
  MarketsSummaryStoreView,
  useMarketsSummaryStoreView,
} from '#/features/perpetuals/market-info/markets-summary-context';
import {
  MarketsStoreView,
  usePerpetualMarketsStoreView,
} from '#/features/perpetuals/market-info/markets-context';

import createSafeContext from '#/utils/createSafeContext';
import nonNullable from '#/utils/nonNullable';

import { usdCompactFormatter } from './utils';

interface FutureMarketView {
  readonly symbol: string;
  readonly lastTradedPrice: string;
  readonly openInterest: string;
  readonly volume24h: string;
}

export interface MarketsMenuView {
  readonly status: 'loading' | 'error' | 'connected' | 'disconnected';
  readonly error: string;
  readonly futureMarkets: FutureMarketView[];
}

export const [useMarketsMenuView, ViewProvider] =
  createSafeContext<MarketsMenuView>('MarketsMenuView');

interface Props {
  readonly children: ReactNode;
}

export function MarketsMenuProvider(props: Props) {
  const perpetualMarketsStoreView = usePerpetualMarketsStoreView();
  const marketsSummaryStoreView = useMarketsSummaryStoreView();

  const view = useMemo(
    () => createView(perpetualMarketsStoreView, marketsSummaryStoreView),
    [marketsSummaryStoreView, perpetualMarketsStoreView],
  );

  return <ViewProvider value={view}>{props.children}</ViewProvider>;
}

function createView(
  marketsStore: MarketsStoreView,
  marketsSummaryStore: MarketsSummaryStoreView,
): MarketsMenuView {
  const { status, error } = statusView(marketsStore, marketsSummaryStore);
  const futureMarkets = futureMarketsView(marketsStore, marketsSummaryStore);

  return { futureMarkets, status, error };
}

function futureMarketsView(
  marketsStore: MarketsStoreView,
  marketsSummaryStore: MarketsSummaryStoreView,
): MarketsMenuView['futureMarkets'] {
  const marketsSummary = marketsStore
    .getAllMarkets()
    .map((market) => marketsSummaryStore.getMarketSummary(market.symbol))
    .filter(nonNullable)
    .sort((a, b) => b.volume_24h.comparedTo(a.volume_24h));

  return marketsSummary.map(futureMarketView);
}

function futureMarketView(
  marketSummary: PerpetualMarketSummary,
): FutureMarketView {
  const lastTradedPrice = marketSummary.last_traded_price
    ? `$${marketSummary.last_traded_price.toFormat()}`
    : '-';

  const openInterest =
    marketSummary.oracle_price != null
      ? usdCompactFormatter.format(
          marketSummary.open_interest
            .times(marketSummary.oracle_price)
            .toNumber(),
        )
      : '-';

  const volume24h = usdCompactFormatter.format(
    marketSummary.volume_24h.toNumber(),
  );

  return {
    symbol: marketSummary.symbol,
    lastTradedPrice,
    openInterest,
    volume24h,
  };
}

function statusView(
  marketsStore: MarketsStoreView,
  marketsSummaryStore: MarketsSummaryStoreView,
): { status: MarketsMenuView['status']; error: MarketsMenuView['error'] } {
  if (
    marketsStore.status === 'error' ||
    marketsSummaryStore.status === 'error'
  ) {
    const errors = [marketsStore.error, marketsSummaryStore.error];
    const errorMessage = errors.join('\n');
    return { status: 'error', error: errorMessage };
  }

  if (
    marketsStore.status === 'loading' ||
    marketsSummaryStore.status === 'loading'
  ) {
    return { status: 'loading', error: '' };
  }

  if (marketsSummaryStore.status === 'disconnected') {
    return { status: 'disconnected', error: '' };
  }

  return { status: 'connected', error: '' };
}
